import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ConnectionStatus from './components/ConnectionStatus';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import PackingStation from './pages/PackingStation';
import SearchDashboard from './pages/SearchDashboard';
import DailyDashboard from './pages/DailyDashboard';
import ReportsDashboard from './pages/ReportsDashboard';
import ActivityLogs from './pages/ActivityLogs';
import Settings from './pages/Settings';
import MobilePackingStation from './pages/MobilePackingStation';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], noLayout = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === 'executive') {
      return <Navigate to="/packing" replace />;
    } else if (user.role === 'manager') {
      return <Navigate to="/search" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return noLayout ? children : <Layout>{children}</Layout>;
};

// Auth Route - Redirect if already logged in
const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'executive') {
      return <Navigate to="/packing" replace />;
    } else if (user.role === 'manager') {
      return <Navigate to="/search" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Default Route Handler
const DefaultRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'executive') {
    return <Navigate to="/packing" replace />;
  } else if (user.role === 'manager') {
    return <Navigate to="/search" replace />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Route */}
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/packing" element={
        <ProtectedRoute allowedRoles={['admin', 'executive']}>
          <PackingStation />
        </ProtectedRoute>
      } />
      
      <Route path="/search" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <SearchDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/daily" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <DailyDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <ReportsDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/logs" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <ActivityLogs />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />

      {/* Mobile Packing Station — full screen, no Layout */}
      <Route path="/mobile-packing" element={
        <ProtectedRoute allowedRoles={['admin', 'executive']} noLayout>
          <MobilePackingStation />
        </ProtectedRoute>
      } />

      {/* Default Route */}
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConnectionStatus />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#18181b',
              border: '1px solid #27272a',
              color: '#fafafa'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
